import {LanguageType} from '@shared-types';
import {Expose, Transform, Type} from 'class-transformer';
import {IsArray, IsBoolean, IsEnum, IsNumber, IsOptional, IsString} from 'class-validator';
import {UserPreferencesFinderSchema} from '../schemas/user-preferences-finder.schema';
import {UserPreferDateFormatType, tryConvertToUserPreferDateFormatType} from '../types/user-preferences-date-format.type';

export class UserPreferences {
	@Expose() @IsEnum(LanguageType) @Transform(({value}) => value ?? LanguageType.Hebrew) language: LanguageType = LanguageType.Hebrew;
	@Expose() @IsEnum(UserPreferDateFormatType) @Transform(({value}) => value ?? UserPreferDateFormatType.DDMMYYYY) dateFormat: UserPreferDateFormatType = UserPreferDateFormatType.DDMMYYYY;
	@Expose() @IsBoolean() @Transform(({value}) => value ?? true) notifyEmail: boolean = true;
	@Expose() @IsBoolean() @Transform(({value}) => value ?? false) notifySMS: boolean = false;
	@Expose() @IsBoolean() @Transform(({value}) => value ?? false) notifyWhatsapp: boolean = false;
	@Expose() @IsNumber() @Transform(({value}) => value ?? 7) daysToReminder: number = 7;
	@Expose() @IsString() @Transform(({value}) => value ?? '#000000') penColor: string = '#000000';
	@Expose() @IsNumber() @Transform(({value}) => value ?? 2) penWidth: number = 2;
	@Expose() @IsNumber() @Transform(({value}) => value ?? 1) defaultStamp: number = 1;
	@Expose() @IsArray() @IsString({each: true}) @Transform(({value}) => value ?? []) showMetaTags: string[] = [];
	@Expose() @IsBoolean() @Transform(({value}) => value ?? true) applyUserPreferences: boolean = true;
	@Expose() @IsArray() @IsString({each: true}) @Transform(({value}) => value ?? []) viewedTutorials: string[] = [];
	@Expose() @Transform(({value}) => value ?? {}) tables: Record<string, string[]> = {};
	@Expose() @IsString() @IsOptional() @Transform(({value}) => value ?? '') defaultCyclesFilter: string = '';
	@Expose() @Type(() => UserPreferencesFinderSchema) @Transform(({value}) => value ?? new UserPreferencesFinderSchema()) finder: UserPreferencesFinderSchema = new UserPreferencesFinderSchema();

	constructor(init?: DeepPartial<UserPreferences>) {
		// Initialize finder first, possibly with nested data from init
		this.finder = new UserPreferencesFinderSchema(init?.finder);

		// Apply other properties from init
		if (init) {
			Object.assign(this, {
				...init,
				finder: this.finder, // Ensure the constructed finder is used
			});
		}
		// Note: Default values are now handled by initializers and @Transform decorators
	}

	public static fromIUserPreferences(preferences: Partial<UserPreferences>): UserPreferences {
		return new UserPreferences({
			language: preferences.language,
			dateFormat: tryConvertToUserPreferDateFormatType(preferences.dateFormat),
			notifyEmail: preferences.notifyEmail,
			notifySMS: preferences.notifySMS,
			notifyWhatsapp: preferences.notifyWhatsapp,
			daysToReminder: preferences.daysToReminder,
			penColor: preferences.penColor,
			penWidth: preferences.penWidth,
			defaultStamp: preferences.defaultStamp,
			showMetaTags: preferences.showMetaTags,
			applyUserPreferences: preferences.applyUserPreferences,
			viewedTutorials: preferences.viewedTutorials,
			tables: preferences.tables,
			defaultCyclesFilter: preferences.defaultCyclesFilter,
			finder: new UserPreferencesFinderSchema(
				// Convert dates to strings in the finder object if they exist
				preferences.finder
					? {
							...preferences.finder,
							selectedColumns: preferences.finder.selectedColumns
								? Array.isArray(preferences.finder.selectedColumns)
									? preferences.finder.selectedColumns.map(col => (typeof col === 'string' ? col : JSON.stringify(col)))
									: []
								: [],
							columnOrder: preferences.finder.columnOrder
								? Array.isArray(preferences.finder.columnOrder)
									? preferences.finder.columnOrder.map(col => (typeof col === 'string' ? col : JSON.stringify(col)))
									: []
								: [],
							lastFilter: preferences.finder.lastFilter
								? {
										...preferences.finder.lastFilter,
										tags: preferences.finder.lastFilter.tags
											? Array.isArray(preferences.finder.lastFilter.tags)
												? preferences.finder.lastFilter.tags.map(tag => (typeof tag === 'string' ? tag : JSON.stringify(tag)))
												: []
											: [],
									}
								: undefined,
						}
					: undefined,
			),
		});
	}

	public update(updates: DeepPartial<UserPreferences>): void {
		// Handle potential update to the nested finder object
		if (updates.finder) {
			// If finder exists, update it; otherwise, create a new one
			if (this.finder) {
				Object.assign(this.finder, updates.finder);
			} else {
				this.finder = new UserPreferencesFinderSchema(updates.finder);
			}
			// Prevent overwriting the nested object with the partial update object
			delete updates.finder;
		}
		Object.assign(this, updates);
	}

	public static isUserPreferences(preferences: any): preferences is UserPreferences {
		return [
			'language',
			'dateFormat',
			'notifyEmail',
			'notifySMS',
			'notifyWhatsapp',
			'daysToReminder',
			'penColor',
			'penWidth',
			'defaultStamp',
			'showMetaTags',
			'applyUserPreferences',
			'viewedTutorials',
			'tables',
			'defaultCyclesFilter',
			'finder',
		].every(key => key in preferences);
	}
}

// Partial models for minimal user preferences
export class MinimalUserPreferences {
	@Expose() @IsBoolean() @Transform(({value}) => value ?? true) notifyEmail: boolean = true;

	@Expose() @IsBoolean() @Transform(({value}) => value ?? false) notifySMS: boolean = false;

	@Expose() @IsBoolean() @Transform(({value}) => value ?? false) notifyWhatsapp: boolean = false;

	constructor(init?: Partial<MinimalUserPreferences>) {
		if (init) {
			Object.assign(this, init);
		}
	}

	public static isMinimalUserPreferences(preferences: any): preferences is MinimalUserPreferences {
		return preferences === null || ['notifyEmail', 'notifySMS', 'notifyWhatsapp'].every(key => key in preferences);
	}
}
